import { Button, Callout, Dialog, DialogBody, DialogFooter, Intent, Label } from '@blueprintjs/core'
import React from 'react'
import Select from 'react-select'
import Client from '../../../client'
import APIErrorCallout from '../../../components/errors/APIErrorCallout'
import ListingTypes from '../../../constants/ebay/ListingTypes'
import DEFAULT_END_TIMES from '../../../constants/shopgoodwill/DefaultEndTimes'
import { showToast } from '../../../Toast'
import { S, localStringToUtcString, readQueryParams } from '../../../utils'
import { BuyItNowPrice, ListingDuration, ListingType, StartingPrice } from '../../products_v4/listings/ebay/Inputs'
import { trackNeedsAttentionListingEvent } from '../../home/v2/utils'

interface RelistDialogState {
  error: any
  loading: boolean
  form: {
    startingPrice: string
    buyItNowPrice: string
    listingType: string
    listingDuration: string
    scheduleTime?: string
  }
}

interface RelistDialogProps {
  data: any
  actionTitle?: any
  isOpen: boolean
  currencyCode?: string
  recycledProductId?: any
  onProductRestored?: any
  onClose: any
  onSubmit?: Function
  hasSold?: boolean
  marketAccountId: number
}

export default class RelistDialog extends React.Component<RelistDialogProps, RelistDialogState> {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      loading: false,
      form: { startingPrice: '', buyItNowPrice: '', listingType: '', listingDuration: '' }
    }
  }

  componentDidMount() {
    this.fetchDefaults()
  }

  fetchDefaults = async () => {
    this.setState({ loading: true })
    const res = await Client.getAllEbaySettings()
    const { ebaySettings } = res.data
    // every ebay account will have settings built out, but let's be careful
    const { settings } = ebaySettings.filter(setting => setting.account.id === this.props.marketAccountId)[0] || {}
    this.setState({
      loading: false,
      form: {
        startingPrice: settings.defaultStartingPrice,
        buyItNowPrice: null,
        listingDuration: settings.defaultListingDuration,
        listingType: settings.defaultListingType,
        scheduleTime: settings.defaultLocalScheduleTime
      }
    })
  }

  relist = async () => {
    const { data } = this.props
    this.setState({ loading: true, error: null })
    try {
      const res = await Client.bulkRelistEbay(data, {
        ...this.state.form,
        scheduleTime: localStringToUtcString(this.state.form.scheduleTime)
      })
      if (readQueryParams().tab === 'attention-required') trackNeedsAttentionListingEvent('re-list', data.length)
      this.setState({ loading: false }, () => {
        showToast(`Relisting ${data.length} item${S(data)}...`, Intent.PRIMARY)
        this.props.onClose(true, res.data.failed)
      })
    } catch (error) {
      this.setState({ loading: false, error: error.response })
    }
  }

  maybeCustomRelist = async () => {
    if (this.props.onSubmit) {
      this.setState({ loading: true, error: null })
      this.props.onSubmit(this.state.form, error => {
        this.setState({ loading: false, error })
        if (!error) this.props.onClose(true)
      })
    } else {
      this.relist()
    }
  }

  restoreProduct = async () => {
    const { data, recycledProductId } = this.props
    try {
      await Client.recycleProduct(recycledProductId, false)
      // This callback isn't meant to be called in bulk, therefor assume an array of 1 will be supplied
      if (this.props.onProductRestored) this.props.onProductRestored(data[0])
    } catch (error) {
      this.setState({ loading: false, error: error.response })
    }
  }

  onFormChange = (name, value) => {
    this.setState(s => ({
      form: {
        ...s.form,
        [name]: value
      }
    }))
  }

  onClose = () => {
    this.setState(
      {
        loading: false,
        error: null
      },
      this.props.onClose
    )
  }

  onChangeListingType = v => {
    this.setState(s => ({
      form: {
        ...s.form,
        listingType: v,
        listingDuration: v === ListingTypes.FixedPriceItem.id ? 'GTC' : s.form.listingDuration,
        buyItNowPrice: v === ListingTypes.Chinese.id ? s.form.buyItNowPrice : null
      }
    }))
  }

  render() {
    const { isOpen, data, recycledProductId, currencyCode = 'USD' } = this.props
    const { form, loading, error } = this.state

    return (
      <Dialog isOpen={isOpen} onClose={this.onClose} title={this.props.actionTitle || 'Relist'}>
        {error && <APIErrorCallout response={error} />}
        {recycledProductId && (
          <Callout intent={Intent.DANGER}>
            This listing&apos;s product is <strong>recycled</strong>. You must restore it first.
            <br />
            <Button style={{ marginTop: 5 }} small intent={Intent.PRIMARY} onClick={this.restoreProduct}>
              Restore
            </Button>
          </Callout>
        )}
        {this.props.hasSold && (
          <Callout intent={Intent.WARNING}>
            You have selected a <strong>Sold</strong> listing. Please check to make sure this was intentional
          </Callout>
        )}
        <DialogBody>
          <p style={{ marginBottom: 20 }}>Leave fields blank to use current values</p>
          <ListingType
            clearable
            placeholder="Use current"
            value={form.listingType}
            onChange={this.onChangeListingType}
          />
          <ListingDuration
            clearable
            placeholder="Use current"
            value={form.listingDuration}
            onChange={v => this.onFormChange('listingDuration', v)}
            disabled={form.listingType === 'FixedPriceItem'}
          />
          <Label className="bp5-label">
            Listing Start Time{' '}
            <div>
              <Select
                placeholder="Select"
                backspaceRemovesValue
                options={DEFAULT_END_TIMES.map(v => ({ value: v, label: v }))}
                value={form.scheduleTime}
                onChange={v => this.onFormChange('scheduleTime', v ? v.value : null)}
              />
            </div>
          </Label>
          {form.listingType === ListingTypes.Chinese.id ? (
            <StartingPrice
              placeholder="Use current"
              value={form.startingPrice}
              onChange={v => this.onFormChange('startingPrice', v)}
              currencyCode={currencyCode}
            />
          ) : (
            <BuyItNowPrice
              placeholder="Use current"
              value={form.startingPrice}
              onChange={v => this.onFormChange('startingPrice', v)}
              currencyCode={currencyCode}
            />
          )}
          {form.listingType === ListingTypes.Chinese.id && (
            <BuyItNowPrice
              placeholder="Use current"
              value={form.buyItNowPrice}
              disabled={form.listingType !== ListingTypes.Chinese.id}
              onChange={v => this.onFormChange('buyItNowPrice', v)}
              currencyCode={currencyCode}
            />
          )}
        </DialogBody>

        <DialogFooter
          actions={
            <Button
              loading={loading}
              intent={Intent.PRIMARY}
              onClick={this.maybeCustomRelist}
              disabled={recycledProductId}
            >
              {this.props.actionTitle || 'Relist'} {data.length} item{S(data)}
            </Button>
          }
        />
      </Dialog>
    )
  }
}
